import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ToggleOn, ToggleOff } from "@mui/icons-material";

import { useGetCoupons } from "../../hooks/coupons/useGetCoupons";
import { useDeleteCoupon } from "../../hooks/coupons/useDeleteCoupon";
import useTableActions from "../../hooks/common/useTableActions";

import TableLayout from "../global/Table/TableLayout";
import { CouponsTableColumns } from "./Table/CouponsTableColumns";
import CouponsTableRow from "./Table/CouponsTableRow";
import { usePermissions } from "../../auth/PermissionsContext";
import BulkActionBar from "../bulk/BulkActionBar";
import BulkResultDialog from "../bulk/BulkResultDialog";
import useBulkClientIterate from "../bulk/useBulkClientIterate";
import { updateCoupon } from "../../api/apiCoupons";

const CouponsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();
    const navigate = useNavigate();

    const perms = useMemo(
        () => ({
            list: has("cms.manage"),
            create: has("cms.manage"),
            edit: has("cms.manage"),
            delete: has("cms.manage"),
        }),
        [has]
    );

    // Filter States
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteCouponMutate, isDeleting } = useDeleteCoupon();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/coupons",
        deleteSingle: deleteCouponMutate,
    });

    const params = useMemo(() => {
        return {
            page,
            per_page: perPage,
            search: search || undefined,
            effective_status: statusFilter !== "" ? statusFilter : undefined,
            sort_by: "created_at",
            sort_order: order,
        };
    }, [page, perPage, search, order, statusFilter]);

    const { coupons, meta, isLoading, error, refetch } = useGetCoupons(params);

    const queryClient = useQueryClient();
    const { run: runBulk, isRunning: isBulkRunning } = useBulkClientIterate();
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkRequested, setBulkRequested] = useState(0);

    // Snapshot the selected coupons' current is_active state so the bar can
    // gray out actions that would be no-ops (e.g. Activate when every
    // selected row is already active). Skip rows that aren't on the
    // current page — we don't have data for them.
    const selectedCoupons = coupons.filter((c) => selectedIds.includes(c.id));
    const knownActiveCount = selectedCoupons.filter((c) => c.is_active).length;
    const knownInactiveCount = selectedCoupons.length - knownActiveCount;
    const allKnownActive = selectedCoupons.length > 0 && knownInactiveCount === 0;
    const allKnownInactive = selectedCoupons.length > 0 && knownActiveCount === 0;

    const handleBulkActivate = async (active) => {
        if (!selectedIds.length) return;
        // Filter out rows that are already in the target state to save
        // round-trips and avoid touching their updated_at unnecessarily.
        const idsToHit = selectedCoupons.length
            ? selectedCoupons.filter((c) => Boolean(c.is_active) !== active).map((c) => c.id)
            : selectedIds;
        if (idsToHit.length === 0) return;
        setBulkRequested(idsToHit.length);
        const result = await runBulk(idsToHit, (id) =>
            updateCoupon(id, { is_active: active })
        );
        queryClient.invalidateQueries({ queryKey: ["coupons"] });
        setBulkResult(result);
    };

    const tableFilters = [
        {
            name: "status",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: (val) => setStatusFilter(val),
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "active", label: { ar: "نشط", en: "Active" } },
                { value: "inactive", label: { ar: "غير نشط", en: "Inactive" } },
                { value: "not_started", label: { ar: "لم يبدأ", en: "Not Started" } },
                { value: "expired", label: { ar: "منتهي", en: "Expired" } },
                { value: "used_up", label: { ar: "استُنفد", en: "Limit Reached" } },
            ]
        }
    ];

    const renderRow = (rowParams) => (
        <CouponsTableRow
            key={rowParams.item.id}
            coupon={rowParams.item}
            index={rowParams.index}
            isSelected={selectedIds.includes(rowParams.item.id)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
        />
    );

    const bulkActions = perms.edit
        ? [
            {
                key: "activate",
                label: isArabic ? "تفعيل" : "Activate",
                icon: <ToggleOn />,
                color: "success",
                disabled: isBulkRunning || allKnownActive,
                tooltip: allKnownActive
                    ? (isArabic ? 'كل المحدد مُفعّل بالفعل' : 'All selected are already active')
                    : '',
                onClick: () => handleBulkActivate(true),
            },
            {
                key: "deactivate",
                label: isArabic ? "إيقاف" : "Deactivate",
                icon: <ToggleOff />,
                color: "warning",
                variant: "outlined",
                disabled: isBulkRunning || allKnownInactive,
                tooltip: allKnownInactive
                    ? (isArabic ? 'كل المحدد متوقف بالفعل' : 'All selected are already inactive')
                    : '',
                onClick: () => handleBulkActivate(false),
            },
        ]
        : [];

    return (
        <>
            <BulkActionBar
                selectedCount={selectedIds.length}
                actions={bulkActions}
                onClear={() => handleSelectAllClick({ target: { checked: false } }, coupons)}
            />
            <BulkResultDialog
                open={Boolean(bulkResult)}
                result={bulkResult}
                totalRequested={bulkRequested}
                onClose={() => setBulkResult(null)}
            />
        <TableLayout
            title={isArabic ? "الكوبونات" : "Coupons"}
            subtitle={isArabic ? "إدارة كوبونات الخصم والرموز الترويجية" : "Manage discount coupons and promo codes"}
            search={search}
            onSearchChange={setSearch}
            addButtonLabel={isArabic ? "إضافة كوبون" : "Add Coupon"}
            onAddClick={() => navigate("/coupons/create")}
            addButtonVisible={perms.create}
            isLoading={isLoading}
            error={error}
            items={coupons}
            meta={meta}
            page={page}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            order={order}
            onRequestSort={handleRequestSort}
            columns={CouponsTableColumns}
            renderRow={renderRow}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            menuDisableEdit={!perms.edit}
            menuDisableDelete={!perms.delete}
            isDeletingSingle={isDeleting}
            selectedIds={selectedIds}
            onSelectAllClick={(e) => handleSelectAllClick(e, coupons)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onBulkDelete={handleBulkDelete}
            bulkDeleteDisabled={!perms.delete}
            filters={tableFilters}
            onClearAllFilters={() => {
                setStatusFilter("");
            }}
            emptyLabelAr={isArabic ? "لا توجد كوبونات" : "No coupons found"}
            emptyLabelEn={isArabic ? "لا توجد كوبونات" : "No coupons found"}
            refetch={refetch}
        />
        </>
    );
};

export default CouponsTable;
